import { useState } from 'react';
import { customInstance } from '@edison/api-client-react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { AlertDialog } from '@/components/ui/alert-dialog.js';
import type { EligibilityStatus } from './eligibility-chip.js';

// Result envelope from POST /api/cost-allocations/bulk-eligibility (dry-run or executed).
export interface BulkEligibilityResult {
  dryRun: boolean;
  matched: number;
  updated: number;
  skippedHardExcluded: number;
  totals: { amount: number; claimableBefore: number; claimableAfter: number; claimableDelta: number };
}

// Source options a reviewer can bulk-act on. Kept in step with COST_SOURCES on the server route
// (the filter is an enum there — a typo'd source can never silently match zero rows).
const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any source' },
  { value: 'api_aws', label: 'AWS (api_aws)' },
  { value: 'api_gcp', label: 'GCP (api_gcp)' },
  { value: 'api_openai', label: 'OpenAI (api_openai)' },
  { value: 'api_anthropic', label: 'Anthropic (api_anthropic)' },
  { value: 'api_replit', label: 'Replit (api_replit)' },
  { value: 'api_xero', label: 'Xero GL (api_xero)' },
  { value: 'manual', label: 'Manual (manual)' },
];

// Presets. The standing double-count banner / per-vendor "Resolve: keep Xero GL" badge surface is a
// deferred item on this page, so the resolve affordance lives here as a preset (Task 3 fallback):
// it pre-fills the exclude decision + stock reason; the reviewer still picks the source + vendor +
// period to scope it to the double-dipped rows.
const XERO_SUPERSEDE_REASON = 'Superseded by Xero GL actuals — double-count resolution';

const fullAud = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);

export function BulkEligibilityPanel({
  open,
  onClose,
  incomeYear,
  defaultPeriodStart,
  defaultPeriodEnd,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  incomeYear: string;
  defaultPeriodStart: string;
  defaultPeriodEnd: string;
  onSuccess: () => void;
}) {
  const emptyForm = {
    preset: 'none' as 'none' | 'xero_supersede',
    source: 'api_aws',
    vendor: '',
    periodStart: defaultPeriodStart,
    periodEnd: defaultPeriodEnd,
    filterStatus: '' as '' | EligibilityStatus,
    setStatus: 'excluded' as EligibilityStatus,
    claimablePercent: '',
    reason: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  // Non-null result = the REVIEW step: an AlertDialog over the form showing the dry-run impact.
  const [result, setResult] = useState<BulkEligibilityResult | null>(null);

  const reset = () => {
    setForm({ ...emptyForm, periodStart: defaultPeriodStart, periodEnd: defaultPeriodEnd });
    setPhase('idle');
    setError(null);
    setResult(null);
  };

  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const applyPreset = (preset: 'none' | 'xero_supersede') => {
    if (preset === 'xero_supersede') {
      setForm((f) => ({ ...f, preset, setStatus: 'excluded', reason: XERO_SUPERSEDE_REASON, claimablePercent: '' }));
    } else {
      setForm((f) => ({ ...f, preset }));
    }
  };

  const buildBody = (dryRun: boolean) => ({
    filter: {
      source: form.source || undefined,
      vendor: form.vendor.trim().toLowerCase() || undefined,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      eligibilityStatus: form.filterStatus || undefined,
    },
    set: {
      eligibilityStatus: form.setStatus,
      claimablePercent:
        form.setStatus === 'claimable' && form.claimablePercent.trim() !== ''
          ? parseFloat(form.claimablePercent)
          : undefined,
      eligibilityReason: form.reason.trim(),
    },
    dryRun,
  });

  const runDryRun = async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await customInstance<{ data: BulkEligibilityResult }>({
        url: '/api/cost-allocations/bulk-eligibility',
        method: 'POST',
        data: buildBody(true),
      });
      setResult(res.data);
      setPhase('idle');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Failed to preview the bulk update. Please try again.');
      setPhase('error');
    }
  };

  const execute = async () => {
    setPhase('loading');
    setError(null);
    try {
      await customInstance({
        url: '/api/cost-allocations/bulk-eligibility',
        method: 'POST',
        data: buildBody(false),
      });
      onSuccess();
      close();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Failed to apply the bulk update. Please try again.');
      setPhase('error');
    }
  };

  const backToForm = () => { setResult(null); setPhase('idle'); setError(null); };

  const canReview =
    (form.source !== '' || form.vendor.trim() !== '') &&
    form.periodStart !== '' &&
    form.periodEnd !== '' &&
    form.reason.trim().length > 0;

  const fieldCls = 'w-full rounded border bg-background px-3 py-2 text-sm';
  const labelCls = 'text-xs font-medium block mb-1';

  const verb = form.setStatus === 'excluded' ? 'exclude' : form.setStatus === 'claimable' ? 'mark claimable' : 'move to needs-review';
  const inReview = result !== null;
  const allSkipped = inReview && result.matched > 0 && result.updated === 0;
  const canConfirm = inReview && result.updated > 0;

  return (
    <>
      <SlidePanel open={open} onClose={close} title="Bulk update eligibility" width="w-full max-w-md">
        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            Apply one eligibility decision to every cost row matching the filter in {incomeYear.replace('FY', 'FY ')}.
            You will preview the exact impact before anything is written.
          </p>

          <div>
            <label className={labelCls} htmlFor="be-preset">Preset</label>
            <select id="be-preset" className={fieldCls} value={form.preset}
              onChange={(e) => applyPreset(e.target.value as 'none' | 'xero_supersede')}>
              <option value="none">None (custom)</option>
              <option value="xero_supersede">Supersede per-vendor rows with Xero GL</option>
            </select>
            {form.preset === 'xero_supersede' && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Sets the decision to <strong>excluded</strong> with the double-count resolution reason. Pick the
                source (api_* / manual) and vendor of the rows Xero GL now covers.
              </p>
            )}
          </div>

          <div className="rounded-md border p-3 space-y-3 bg-muted/20">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Which rows</p>
            <div>
              <label className={labelCls} htmlFor="be-source">Source</label>
              <select id="be-source" className={fieldCls} value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                {SOURCE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="be-vendor">Vendor (optional)</label>
              <input id="be-vendor" type="text" className={fieldCls} maxLength={50}
                value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                placeholder="e.g. aws, anthropic" />
            </div>
            <p className="text-[11px] text-muted-foreground">At least one of source or vendor is required.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="be-start">Period start</label>
                <input id="be-start" type="date" className={fieldCls} value={form.periodStart}
                  onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls} htmlFor="be-end">Period end</label>
                <input id="be-end" type="date" className={fieldCls} value={form.periodEnd}
                  onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="be-current">Only rows currently (optional)</label>
              <select id="be-current" className={fieldCls} value={form.filterStatus}
                onChange={(e) => setForm((f) => ({ ...f, filterStatus: e.target.value as '' | EligibilityStatus }))}>
                <option value="">Any status</option>
                <option value="claimable">Claimable</option>
                <option value="needs_review">Needs review</option>
                <option value="excluded">Excluded</option>
              </select>
            </div>
          </div>

          <div className="rounded-md border p-3 space-y-3 bg-muted/20">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Set them to</p>
            <fieldset>
              <legend className={labelCls}>New status</legend>
              <div className="flex flex-col gap-1.5">
                {(['excluded', 'claimable', 'needs_review'] as EligibilityStatus[]).map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="be-set-status" value={s} checked={form.setStatus === s}
                      onChange={() => setForm((f) => ({ ...f, setStatus: s }))} />
                    <span className="capitalize">{s.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            {form.setStatus === 'claimable' && (
              <div>
                <label className={labelCls} htmlFor="be-percent">Claimable % (optional — leaves each row's % if blank)</label>
                <input id="be-percent" type="number" step="0.1" min="0" max="100" className={fieldCls}
                  value={form.claimablePercent} onChange={(e) => setForm((f) => ({ ...f, claimablePercent: e.target.value }))}
                  placeholder="e.g. 100" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Setting a % stamps the apportionment basis as a manual reviewer override.
                </p>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="be-reason">
                Reason <span className="text-destructive">*</span>
              </label>
              <textarea id="be-reason" className={`${fieldCls} resize-none`} rows={3} maxLength={2000}
                value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Superseded by Xero GL actuals (Subscriptions account)" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Required for every bulk change — this is the working-papers record of the override.
              </p>
            </div>
          </div>

          {phase === 'error' && error && !inReview && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="primary" className="flex-1" disabled={!canReview || phase === 'loading'}
              onClick={() => void runDryRun()}>
              {phase === 'loading' && !inReview ? 'Previewing…' : 'Review changes'}
            </Button>
            <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
          </div>
        </div>
      </SlidePanel>

      {/* REVIEW step: destructive confirmation over the form — real dry-run figures, never estimates. */}
      {inReview && (
        <AlertDialog
          open
          title="Review bulk eligibility change"
          destructive
          busy={phase === 'loading'}
          cancelLabel="Back"
          onCancel={backToForm}
          onConfirm={canConfirm ? () => void execute() : undefined}
          confirmLabel={canConfirm
            ? (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Confirm — {verb} {result.updated} row{result.updated === 1 ? '' : 's'}
              </>
            )
            : undefined}
        >
          {result.matched === 0 ? (
            <div className="text-center py-4">
              <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" aria-hidden="true" />
              <p className="text-sm font-medium">No rows match this filter.</p>
              <p className="text-xs text-muted-foreground mt-1">Adjust the source, vendor, or period and try again.</p>
            </div>
          ) : allSkipped ? (
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm">
                All {result.matched} matched row{result.matched === 1 ? ' is' : 's are'} ATO hard-excluded and will be
                skipped — nothing to update.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold">
                    This will {verb} {result.updated} row{result.updated === 1 ? '' : 's'} totalling {fullAud(result.totals.amount)}.
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Claimable impact{' '}
                    <span className={result.totals.claimableDelta < 0 ? 'text-destructive font-medium' : 'text-emerald-700 font-medium'}>
                      {result.totals.claimableDelta >= 0 ? '+' : '−'}{fullAud(Math.abs(result.totals.claimableDelta))}
                    </span>{' '}
                    ({fullAud(result.totals.claimableBefore)} → {fullAud(result.totals.claimableAfter)})
                  </p>
                </div>
              </div>
              {result.skippedHardExcluded > 0 && (
                <p className="text-xs text-amber-700 flex items-center gap-1 pl-7">
                  {result.skippedHardExcluded} ATO hard-excluded row{result.skippedHardExcluded === 1 ? '' : 's'} (R&DTI claim-prep fees) will be skipped — they can never be claimable.
                </p>
              )}
              <p className="text-[11px] text-muted-foreground pl-7">
                Reason: {form.reason.trim()}
              </p>
            </div>
          )}
          {phase === 'error' && error && (
            <p className="text-xs text-destructive flex items-center gap-1 mt-3">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {error}
            </p>
          )}
        </AlertDialog>
      )}
    </>
  );
}
